import { askUrl, authHeaders, ensureSession } from "./api";
import {
  CARD_QUESTIONS,
  MOCK,
  MOCK_ANSWERS,
  MOCK_FALLBACK_ANSWER,
  MOCK_S_PASS_ANSWER,
} from "./mock";
import type { FactKey } from "./types";

export interface AskHandlers {
  onText(answer: string): void;
  onTool(label: string): void;
  onFinal(answer: string): void;
  onError(message: string): void;
}

/**
 * Streams one question through the bound session. Mock mode feeds canned
 * events through the SAME handler surface, so the UI cannot tell them apart.
 */
export async function ask(question: string, cb: AskHandlers): Promise<void> {
  if (MOCK) return mockStream(question, cb);
  await ensureSession();
  const r = await fetch(askUrl(), {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ question }),
  });
  if (!r.ok) return cb.onError("error " + r.status + ": " + (await r.text()));
  const reader = r.body!.getReader();
  const dec = new TextDecoder();
  let buf = "",
    answer = "",
    finalSeen = false;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const chunk = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      let name = "",
        data = "";
      for (const line of chunk.split("\n")) {
        if (line.startsWith("event:")) name = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (!data) continue;
      const d = JSON.parse(data);
      if (name === "text") {
        answer += d.delta || "";
        cb.onText(answer);
      } else if (name === "tool_call") {
        cb.onTool(
          "→ " + d.name + (d.input && d.input.pipeline_id ? " (" + d.input.pipeline_id + ")" : ""),
        );
      } else if (name === "final") {
        finalSeen = true;
        cb.onFinal(d.answer || answer);
      } else if (name === "error") {
        return cb.onError(d.message);
      }
    }
  }
  if (!finalSeen) cb.onFinal(answer);
}

function mockStream(question: string, cb: AskHandlers): Promise<void> {
  const key = (Object.keys(CARD_QUESTIONS) as FactKey[]).find(
    (k) => CARD_QUESTIONS[k] === question,
  );
  const answer = key
    ? MOCK_ANSWERS[key]!
    : /s pass/i.test(question)
      ? MOCK_S_PASS_ANSWER
      : MOCK_FALLBACK_ANSWER;
  return new Promise((resolve) => {
    // Only self-service questions dispatch a pipeline; statutory answers come
    // straight from the clerk (mock mirrors the real event stream).
    if (key) setTimeout(() => cb.onTool("→ run_pipeline (employee-self-service)"), 250);
    setTimeout(() => {
      cb.onFinal(answer);
      resolve();
    }, 800);
  });
}
